<script setup lang="ts">
  import {
    Button,
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
    Kbd,
    ModalSectionHeader,
    ModalSectionHeaderTitle
  } from '@gat-ship/vue-components'
  // Sibling step components share an identical interface and listbox wiring by
  // design (uniform `:source`/`select` contract); only the row markup varies.
  // fallow-ignore-next-line code-duplication
  import { IconAgency, IconCheck } from '@gat-ship/icons'
  import type { StepOption } from './create-portcall-steps'
  import type { StepListSource } from '../../../../composables/usePortcallStepSources'
  import { usePortcallStepListbox } from '../../../../composables/usePortcallStepListbox'
  import { useCreatePortcallPermissions } from '../../../../composables/useCreatePortcallPermissions'
  import PortcallStepSearch from './PortcallStepSearch.vue'
  import PortcallStepLoading from './PortcallStepLoading.vue'
  import PortcallStepEmpty from './PortcallStepEmpty.vue'

  /**
   * The agency list step (Who). Full agency list, client-filtered as the user
   * types; one row per agency with its short name as the sub-line.
   */
  // Sibling step components share an identical props/emit + listbox contract by
  // design; only the manage-* emit name and row markup vary.
  // fallow-ignore-next-line code-duplication
  const props = defineProps<{
    source: StepListSource
    selectedValue?: string
    /** Mobile keyboard is up — collapse the question block to free list room. */
    keyboardOpen?: boolean
  }>()
  const emit = defineEmits<{
    select: [option: StepOption, advance: boolean]
    'update:query': [value: string]
    manageAgencies: []
  }>()

  const { onKeyboardAdvance, onPointerSelect, onSelect, selectImmediate } =
    usePortcallStepListbox(
      props.source,
      (option, advance) => emit('select', option, advance),
      { suppressEnterSelect: () => !!props.keyboardOpen }
    )

  const { canManageAgencies } = useCreatePortcallPermissions()

  function onManageAgencies() {
    if (!canManageAgencies.value) return
    emit('manageAgencies')
  }
</script>

<template>
  <Empty
    v-if="
      !source.loading.value &&
      !source.query.value &&
      source.options.value.length === 0
    "
  >
    <EmptyHeader>
      <EmptyMedia variant="icon">
        <IconAgency class="text-muted-foreground size-4 shrink-0" />
      </EmptyMedia>
      <EmptyTitle>No agencies found</EmptyTitle>
      <EmptyDescription v-if="canManageAgencies">
        No agencies are configured for this tenant.
      </EmptyDescription>
      <EmptyDescription v-else>Contact your administrator</EmptyDescription>
    </EmptyHeader>
    <EmptyContent>
      <Button
        v-if="canManageAgencies"
        variant="outline"
        @click="onManageAgencies"
      >
        Manage agencies
      </Button>
    </EmptyContent>
  </Empty>
  <Command
    v-else
    ref="commandRef"
    class="flex h-auto min-h-0 flex-1 flex-col overflow-hidden bg-transparent"
    @keydown.enter.capture="onKeyboardAdvance"
  >
    <div :class="['flex-none px-6 pb-3', keyboardOpen ? 'pt-4' : 'pt-6']">
      <ModalSectionHeader v-if="!keyboardOpen" class="gap-0">
        <div class="flex items-center gap-2">
          <div
            class="text-muted-foreground mb-2 text-[11px] font-semibold uppercase tracking-wider"
          >
            Agency
          </div>
          <Button
            v-if="canManageAgencies && source.options.value.length > 0"
            class="ml-auto"
            variant="link"
            size="xs"
            @click="onManageAgencies"
          >
            Manage agencies
          </Button>
        </div>
        <ModalSectionHeaderTitle class="text-lg font-semibold leading-tight">
          Which agency is handling this call?
        </ModalSectionHeaderTitle>
      </ModalSectionHeader>
      <PortcallStepSearch
        class="mt-4"
        :model-value="source.query.value"
        :keyboard-open="keyboardOpen"
        placeholder="Filter agencies…"
        is-filter
        @update:model-value="emit('update:query', $event)"
      />
    </div>

    <CommandList class="max-h-none min-h-0 flex-1 px-6 pb-4">
      <PortcallStepLoading v-if="source.loading.value" />

      <CommandGroup
        v-else-if="source.options.value.length"
        class="divide-border divide-y p-0"
      >
        <CommandItem
          v-for="opt in source.options.value"
          :key="opt.value"
          :value="opt.value"
          class="group gap-3 rounded-none px-2 py-3"
          @select="onSelect(opt)"
          @dblclick="selectImmediate(opt)"
          @pointerdown="onPointerSelect"
        >
          <IconAgency class="text-muted-foreground size-4 shrink-0" />
          <div class="min-w-0 flex-1">
            <div
              class="truncate text-sm font-medium"
              :class="selectedValue === opt.value && 'text-primary'"
            >
              {{ opt.label }}
            </div>
            <div v-if="opt.sub" class="text-muted-foreground truncate text-xs">
              {{ opt.sub }}
            </div>
          </div>
          <div class="ml-auto flex items-center gap-2">
            <Kbd class="hidden md:group-data-[highlighted]:inline-flex">↵</Kbd>
            <IconCheck
              v-if="selectedValue === opt.value"
              class="text-primary size-4 shrink-0"
            />
          </div>
        </CommandItem>
      </CommandGroup>

      <PortcallStepEmpty
        v-else
        is-filter
        title="No agencies found"
        empty-text="Try a different search term"
      >
        <Button
          v-if="canManageAgencies"
          variant="outline"
          @click="onManageAgencies"
        >
          Manage agencies
        </Button>
      </PortcallStepEmpty>
    </CommandList>
  </Command>
</template>
